import React, { useContext, useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
    Box,
    Button,
    Container as MaterialContainer,
    Grid,
    UiDialog as Dialog,
} from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { createStructuredSelector } from 'reselect';

import { HeaderContainer, TitleIcon } from 'common/components';
import detectScrollBottom from 'common/hocs/detectScrollBottom';
import { LoadingContext } from 'common/utils/contexts';

import * as actions from './actions';
import { ListItems, ResetPasswordModal, UserForm } from './components';
import * as selectors from './selectors';

const Container = ({
    closeModalProduct,
    displayOnlyBottom,
    filterUsers,
    handleChangePassword,
    handleChangePasswordConfirmation,
    handleClickCreate,
    isFetching,
    isOpenModalProduct,
    isSavingItem,
    paginationData,
    reset,
    users,
}) => {
    const { t } = useTranslation();
    const loadingContext = useContext(LoadingContext);
    const [dialogTitle, setDialogTitle] = useState(null);
    const [filterData, setFilterData] = useState({
        orderBy: 'created_at',
        page: 0,
        pageSize: 25,
        searchText: '',
        sortBy: 'desc',
        status: 'active',
    });

    useEffect(() => {
        filterUsers(filterData);
        return () => reset();
    }, []);

    useEffect(() => {
        if (isEmpty(loadingContext)) return;
        if (isSavingItem)
            loadingContext.openLoading(
                t('Saving change', { count: 2 }).concat('...'),
            );
        else loadingContext.closeLoading();
    }, [isSavingItem]);

    const onChangePassword = (event) => handleChangePassword(event);
    const onChangePasswordConfirmation = (event, password) =>
        handleChangePasswordConfirmation(event, password);

    return (
        <MaterialContainer maxWidth={false}>
            <HeaderContainer>
                <Grid size="grow">
                    <TitleIcon title={t('User', { count: 2 })} />
                </Grid>
            </HeaderContainer>
            <ListItems
                filterData={filterData}
                filterUsers={filterUsers}
                handleClickCreate={handleClickCreate}
                isFetching={isFetching}
                items={users}
                paginationData={paginationData}
                setFilterData={setFilterData}
            />

            {displayOnlyBottom && (
                <Box mt={3} textAlign="right">
                    <Button
                        endIcon={<AddIcon />}
                        onClick={handleClickCreate}
                        sx={{ width: { md: 'auto', xs: '100%' } }}
                    >
                        {t('Add user')}
                    </Button>
                </Box>
            )}

            <Dialog
                onClose={closeModalProduct}
                open={isOpenModalProduct}
                title={dialogTitle}
            >
                <UserForm
                    filterData={filterData}
                    handleClickCloseForm={closeModalProduct}
                    onChangePassword={onChangePassword}
                    onChangePasswordConfirmation={onChangePasswordConfirmation}
                    setDialogTitle={setDialogTitle}
                />
            </Dialog>

            <ResetPasswordModal />
        </MaterialContainer>
    );
};

const mapStateToProps = createStructuredSelector({
    isFetching: selectors.getIsFetchingUsers,
    isOpenModalProduct: selectors.getIsOpenModalProduct,
    isSavingItem: selectors.getIsSavingItem,
    paginationData: selectors.getFetchUsersPagination,
    users: selectors.getFetchUsersData,
});

const mapDispatchToProps = (dispatch) => ({
    closeModalProduct: () => dispatch(actions.closeModalProduct()),
    filterUsers: (filterData) => dispatch(actions.filterUsers(filterData)),
    handleClickCreate: () => dispatch(actions.prepareCreate()),
    reset: () => dispatch(actions.reset()),
});

Container.propTypes = {
    closeModalProduct: PropTypes.func,
    displayOnlyBottom: PropTypes.bool,
    filterUsers: PropTypes.func,
    handleChangePassword: PropTypes.func,
    handleChangePasswordConfirmation: PropTypes.func,
    handleClickCreate: PropTypes.func,
    isFetching: PropTypes.bool,
    isOpenModalProduct: PropTypes.bool,
    isSavingItem: PropTypes.bool,
    paginationData: PropTypes.object,
    reset: PropTypes.func,
    users: PropTypes.array,
};

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    detectScrollBottom(),
)(Container);
